import Masonry from "masonry-layout";
import React from "react";

import { focusElement, getFocusableElems } from "../utils/keyboardFocus";

function getAllTiles(
    containerRef: React.RefObject<HTMLDivElement | null>,
    itemSelector: string,
): HTMLElement[] {
    if (!containerRef.current) {
        return [];
    }
    const elems =
        containerRef.current.querySelectorAll<HTMLElement>(itemSelector);
    return Array.from(elems);
}

function getMaxTileHeight(tiles: HTMLElement[]): number {
    return tiles.reduce((memo, elem) => {
        return Math.max(memo, elem.offsetHeight);
    }, 0);
}

export function MasonryGrid<Entity>(props: {
    appendItems?: boolean;
    className?: string;
    itemSelector: string;
    entities: Entity[];
    getTileID: (entity: Entity) => string;
    buildTile: (
        entity: Entity,
        ref: React.RefObject<HTMLDivElement | null>,
    ) => React.ReactNode;
    emptyState: React.ReactNode;
    options?: Masonry.Options;
    forceEqualHeight?: boolean;
}): React.ReactNode {
    const tileRefsRef = React.useRef<
        Map<string, React.RefObject<HTMLDivElement | null>>
    >(new Map());
    const containerRef = React.useRef<HTMLDivElement>(null);
    const masonryGridRef = React.useRef<Masonry | null>(null);
    const prevEntitiesRef = React.useRef<Entity[]>([]);

    const setTileHeights = () => {
        const tiles = getAllTiles(containerRef, props.itemSelector);
        // Clear set heights so that we can measure the intrinsic height
        tiles.forEach((elem) => {
            elem.style.height = "";
        });
        // Measure the intrinsic height of each tile, then set each tile to the max.
        const height = getMaxTileHeight(tiles);
        tiles.forEach((elem) => {
            elem.style.height = `${height}px`;
        });
    };

    const layout = () => {
        // Set tile heights (if requested)
        if (props.forceEqualHeight) {
            setTileHeights();
        }
        // Relayout grid
        if (masonryGridRef.current?.layout) {
            masonryGridRef.current.layout();
        }
    };

    const reloadItems = () => {
        if (masonryGridRef.current?.reloadItems) {
            masonryGridRef.current.reloadItems();
        }
    };

    const addItems = (
        ...args: Parameters<NonNullable<Masonry["addItems"]>>
    ) => {
        if (masonryGridRef.current?.addItems) {
            masonryGridRef.current.addItems(...args);
        }
    };

    const appended = (
        ...args: Parameters<NonNullable<Masonry["appended"]>>
    ) => {
        if (masonryGridRef.current?.appended) {
            masonryGridRef.current.appended(...args);
        }
    };

    const attachEventHandlers = () => {
        if (!containerRef.current) {
            return;
        }
        // When images load, posts change size, so we need to recalculate the grid layout.
        containerRef.current.querySelectorAll("img").forEach((img) => {
            img.addEventListener("load", () => {
                layout();
            });
        });
    };

    const getOrCreateRef = (
        tileID: string,
    ): React.RefObject<HTMLDivElement | null> => {
        let ref = tileRefsRef.current.get(tileID);
        if (ref) {
            return ref;
        }
        ref = React.createRef<HTMLDivElement>();
        tileRefsRef.current.set(tileID, ref);
        return ref;
    };

    const updateGridLayout = () => {
        const oldNodes: HTMLDivElement[] | null = masonryGridRef.current
            ?.getItemElements
            ? masonryGridRef.current.getItemElements()
            : null;
        if (!containerRef.current || !oldNodes) {
            return;
        }
        const currentNodes =
            containerRef.current.querySelectorAll<HTMLDivElement>(
                props.itemSelector,
            );
        const newNodes = Array.from(currentNodes).filter((node) => {
            return !oldNodes.includes(node);
        });

        if (props.appendItems) {
            // Append new items to the end of layout
            appended(newNodes);
        } else {
            addItems(newNodes);
        }
        attachEventHandlers();
        reloadItems();
        layout();
    };

    const focusFirstNewTile = (prevEntities: Entity[]) => {
        const newEntityArrayIsSuperset = prevEntities.reduce(
            (memo, oldEntity) => {
                return memo && props.entities.indexOf(oldEntity) !== -1;
            },
            true,
        );
        if (!newEntityArrayIsSuperset) {
            return;
        }
        const newEntities = props.entities.filter((newEntity) => {
            return prevEntities.indexOf(newEntity) === -1;
        });
        const entityToFocus: Entity | undefined = newEntities[0];
        if (!entityToFocus) {
            return;
        }
        const tileID = props.getTileID(entityToFocus);
        const tileRef = tileRefsRef.current.get(tileID);
        // Within the chosen tile, find the first focusable element and focus on it.
        if (tileRef && tileRef.current) {
            const firstFocusableElem = getFocusableElems(tileRef.current)[0];
            if (firstFocusableElem) {
                focusElement(firstFocusableElem);
            }
        }
    };

    // componentDidMount
    React.useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        masonryGridRef.current = new Masonry(containerRef.current, {
            itemSelector: props.itemSelector,
            ...props.options,
        });
        // Set tile heights (if requested)
        if (props.forceEqualHeight) {
            setTileHeights();
        }
        attachEventHandlers();

        // componentWillUnmount
        return () => {
            // Destroy the grid layout calculator before unloading
            if (masonryGridRef.current && masonryGridRef.current.destroy) {
                masonryGridRef.current.destroy();
            }
        };
    }, []);

    // componentDidUpdate
    React.useEffect(() => {
        // Skip on initial mount
        if (prevEntitiesRef.current.length === 0 && props.entities.length > 0) {
            prevEntitiesRef.current = props.entities;
            return;
        }

        // Update the grid layout in case theres new items
        updateGridLayout();
        // If there's no tiles currently shown, there's nothing more to do here.
        if (props.entities.length <= 0) {
            prevEntitiesRef.current = props.entities;
            return;
        }
        // Set tile heights (if requested)
        if (props.forceEqualHeight) {
            setTileHeights();
        }
        // Try to focus on first tile of new content (if we just loaded a new page of tiles).
        focusFirstNewTile(prevEntitiesRef.current);

        prevEntitiesRef.current = props.entities;
    }, [
        props.entities,
        props.forceEqualHeight,
        props.itemSelector,
        props.appendItems,
    ]);

    return (
        <div className={`masonry-grid ${props.className}`} ref={containerRef}>
            {props.entities.map((entity) => {
                const tileID = props.getTileID(entity);
                const ref = getOrCreateRef(tileID);
                return props.buildTile(entity, ref);
            })}
            {props.entities.length <= 0 && props.emptyState}
        </div>
    );
}
